import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import 'lazysizes';

import {
  prepareImgixParams,
  defaultProjectImageUrl,
  defaultFirmImageUrl,
} from '../../services/APIClient';
import SegmentClient from '../../services/SegmentClient';

import ReadMoreText from '../ReadMoreText';
import { ANALYTICS_NAME } from './Constants';
import ImageOverPlaceholder, { ASPECT_RATIOS } from '../ImageOverPlaceholder';

const segment = new SegmentClient();

const BrandViewProjectItem = ({
  brandName, project, notes, isLast,
}) => {
  const firmLogoUrl = project.firms.length && project.firms[0].media[0]
    ? prepareImgixParams(project.firms[0].media[0].mediaItem.url, { w: 50, h: 50, auto: 'format,compress' })
    : defaultFirmImageUrl;

  const projectUrl = `${window.location.origin}/projects/${project.slug}/`;
  const firmUrl = project.firms.length ? `${window.location.origin}/firms/${project.firms[0].slug}/` : null;
  const typeUrl = `${window.location.origin}/projects/q/type:`;

  const completionYear = Date.parse(project.completionDate)
    ? new Date(project.completionDate).getFullYear()
    : null;

  // Properties for 'project card clicked' Segment event
  const segmentProperties = {
    // brand_id, brand_name, and brand_slug are added in SegmentClient
    link_url: projectUrl,
    project_id: project.djangoId,
    project_name: project.name,
    project_slug: project.slug,
  };

  return (
    <div className="grid-x grid-padding-x">
      <a href={firmUrl} target="_blank" rel="noreferrer">
        <img
          src={firmLogoUrl}
          className="thumbnail"
          alt={project.firms.length ? project.firms[0].name : 'Firm logo'}
        />
      </a>
      <div className="cell auto">
        <div className="mt-s mb-l">
          {project.firms.length
            ? (
              <>
                <a className="black black-underline" href={firmUrl} target="_blank" rel="noreferrer">
                  {project.firms[0].name}
                </a>
                {' '}
                used
                {' '}
                {brandName}
              </>
            )
            : (
              <>
                {brandName}
                {' '}
                used
                {' '}
              </>
            )}
          {project.tags.length
            ? (
              <>
                &nbsp;for&nbsp;
                {project.tags.length === 1 && 'a '}
                {project.tags.map((v, i) => (
                  <Fragment key={v.tag.slug}>
                    <a
                      className="black black-underline"
                      href={`${typeUrl}${v.tag.parent.slug},${v.tag.slug}/`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {v.tag.text}
                    </a>
                    {i + 1 < project.tags.length && <span>,&nbsp;</span>}
                  </Fragment>
                ))}
              </>
            )
            : <></>}
          {completionYear && ` in ${completionYear}`}
        </div>
        <div className="row">
          <div className="columns small-12 medium-5 large-4 xlarge-3">
            <ImageOverPlaceholder
              image={{
                url: project.media[0] ? project.media[0].mediaItem.url : '',
                width: 220,
                height: 136,
              }}
              placeholder={defaultProjectImageUrl}
              aspectRatio={ASPECT_RATIOS.STANDARD}
              href={projectUrl}
              onClick={() => segment.track(
                ANALYTICS_NAME.BRAND_PROJECT_CARD_CLICKED,
                segmentProperties,
              )}
              borderRadius=".20972rem"
            />
          </div>
          <div className="columns small-12 medium-7 large-8 xlarge-9">
            <a
              className="black black-underline"
              href={`/projects/${project.slug}/`}
              target="_blank"
              rel="noreferrer"
            >
              {project.name}
            </a>
            <div className="ellipsis">{project.location}</div>
            <div className="mt-xl">
              {notes.trim()
                && <i><ReadMoreText text={notes.trim()} length={104} wrapQuotes italic /></i>}
            </div>
          </div>
        </div>
      </div>
      {!isLast && <hr className="small-12" />}
    </div>
  );
};

BrandViewProjectItem.propTypes = {
  brandName: PropTypes.string,
  project: PropTypes.shape({
    name: PropTypes.string,
    slug: PropTypes.string,
    location: PropTypes.string,
    completionDate: PropTypes.string,
    media: PropTypes.arrayOf(PropTypes.shape({
      mediaItem: PropTypes.shape({
        url: PropTypes.string,
      }),
    })),
    firms: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string,
      media: PropTypes.arrayOf(PropTypes.shape({
        mediaItem: PropTypes.shape({
          url: PropTypes.string,
        }),
      })),
    })),
    tags: PropTypes.arrayOf(PropTypes.shape({
      text: PropTypes.string,
      slug: PropTypes.string,
      parent: PropTypes.shape({
        slug: PropTypes.string,
      }),
    })),
  }),
  notes: PropTypes.string,
  isLast: PropTypes.bool,
};

BrandViewProjectItem.defaultProps = {
  brandName: '',
  project: {
    name: '',
    slug: '',
    location: '',
    completionDate: '',
    firms: [{
      name: '',
      slug: '',
      media: [{
        mediaItem: { url: '' },
      }],
    }],
    tags: [{
      text: '',
      slug: '',
      parent: {
        slug: '',
      },
    }],
    media: [{
      mediaItem: { url: '' },
    }],
  },
  notes: '',
  isLast: false,
};

export default BrandViewProjectItem;
